import { useUsers } from '@src/hooks/useUsers';
import { IconName, type FilterableProperty } from '@talos/kyoko';
import { uniq } from 'lodash-es';
import { useMemo } from 'react';

export const CURRENT_USER_SENTINEL = '__CURRENT_USER__';

/**
 * Resolves the CURRENT_USER_SENTINEL in a Users filter array to the actual logged-in user's name.
 * Returns the original array unchanged if the sentinel is not present (fast path).
 */
export function resolveUsersSentinel(users: string[] | undefined, currentUserName: string): string[] | undefined {
  if (!users?.includes(CURRENT_USER_SENTINEL)) {
    return users;
  }
  return users.map(u => (u === CURRENT_USER_SENTINEL ? currentUserName : u));
}

/**
 * Returns true if the row should be excluded by an already-resolved Users filter array.
 * Callers should resolve the sentinel once (e.g. via useMemo) and pass the result here
 * to avoid repeated allocations inside per-row filter callbacks.
 * Pass multiple userValues to allow matching against any of them (e.g. User + RequestUser).
 */
export function isExcludedByResolvedUsers(
  resolvedUsers: string[] | undefined,
  ...userValues: (string | undefined)[]
): boolean {
  if (!resolvedUsers || resolvedUsers.length === 0) {
    return false;
  }
  return !userValues.some(v => v != null && resolvedUsers.includes(v));
}

/**
 * Resolves the CURRENT_USER_SENTINEL sentinel in a Users filter array once per filter/user change,
 * so per-row filter callbacks can use the result without repeated allocations.
 */
export function useResolvedUsers(filterUsers: string[] | undefined, currentUserName: string): string[] | undefined {
  return useMemo(() => resolveUsersSentinel(filterUsers, currentUserName), [filterUsers, currentUserName]);
}

/**
 * This hook creates the filter property definition for Users properties.
 *
 * @returns Filter Property definition to use for Users filters
 */
export function useUsersFilter() {
  const users = useUsers();

  return useMemo(
    () => ({
      ...({
        key: 'Users',
        label: 'User',
        icon: IconName.User,
        options: [CURRENT_USER_SENTINEL, ...uniq(users.map(u => u.Name))],
        getOptionLabel: (option: string) => (option === CURRENT_USER_SENTINEL ? 'Current User' : option),
        optionSorter: (a: string, b: string) => {
          if (a === CURRENT_USER_SENTINEL) {
            return -1;
          }
          if (b === CURRENT_USER_SENTINEL) {
            return 1;
          }
          return a < b ? -1 : a > b ? 1 : 0;
        },
      } as const satisfies FilterableProperty),
    }),
    [users]
  );
}
